import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { AccessRole, AccessRoleDto } from '../../../../core/models/system-settings/manage-roles.model';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperManageRolesService } from '../../../../core/mappers/system-settings/mapper-manage-roles.service';
import { ModalManageRoleComponent } from '../../modals/system-settings/modal-manage-role/modal-manage-role.component';

@Component({
  selector: 'app-manage-roles',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalManageRoleComponent],
  templateUrl: './manage-roles.component.html',
  styleUrl: './manage-roles.component.css'
})
export class ManageRolesComponent implements OnInit {
  roles = signal<AccessRole[]>([]);
  isLoading = signal<boolean>(false);
  isDashboardOpen = signal<boolean>(true);
  Math = Math;

  searchText = signal<string>('');
  isModalOpen = signal<boolean>(false);
  selectedItem = signal<AccessRole | null>(null);
  dashboardStats = signal<any[]>([]);

  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  pageSizeOptions = [20, 50, 100];

  sortColumn = signal<string>('id');
  sortDirection = signal<'asc' | 'desc'>('asc');

  filteredData = computed(() => {
    let filtered = this.roles();
    const search = this.searchText().trim().toLowerCase();

    if (search) {
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(search) ||
        r.id.toString() === search
      );
    }

    const col = this.sortColumn();
    const dir = this.sortDirection();
    if (col) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[col];
        let valB = b[col];

        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return dir === 'asc' ? -1 : 1;
        if (valA > valB) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  });

  totalItems = computed(() => this.filteredData().length);

  filteredAndPaginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredData().slice(start, start + this.pageSize());
  });

  constructor(
    private apiService: ApiSystemSettingsService,
    private mapper: MapperManageRolesService,
    private toast: ToastService
  ) { }

  ngOnInit() {
    this.getAccessRoles();
  }

  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  onRefresh(): void {
    this.searchText.set('');
    this.currentPage.set(1);
    this.getAccessRoles();
  }

  private calculateDashboardStats(): void {
    const roles = this.roles();
    const total = roles.length;
    const activeTotal = roles.filter(r => r.status).length;
    const inactiveTotal = total - activeTotal;

    this.dashboardStats.set([
      { title: 'Role ทั้งหมด', value: total.toLocaleString(), icon: 'badge', color: 'text-amber-500' },
      { title: 'เปิดใช้งาน', value: activeTotal.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
      { title: 'ปิดใช้งาน', value: inactiveTotal.toLocaleString(), icon: 'cancel', color: 'text-rose-500' }
    ]);
  }

  onSearch(): void {
    this.currentPage.set(1);
  }

  sortData(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  navigateToCreate(): void {
    this.selectedItem.set(null);
    this.isModalOpen.set(true);
  }

  navigateToEdit(item: AccessRole): void {
    this.selectedItem.set(item);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedItem.set(null);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / (this.pageSize() || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
  }

  trackById(index: number, item: AccessRole): number {
    return item.id;
  }

  getAccessRoles(): void {
    this.isLoading.set(true);
    this.apiService.getAccessRolesForTable().subscribe({
      next: (dtos: AccessRoleDto[]) => {
        try {
          const list = (dtos || []).map((d: AccessRoleDto) => this.mapper.mapAccessRoleFromDto(d));
          this.roles.set(list);
          this.calculateDashboardStats();
        } catch {
          this.toast.error('โครงสร้างข้อมูลไม่ถูกต้อง');
        } finally {
          this.isLoading.set(false);
        }
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูล Role ได้');
        this.isLoading.set(false);
      }
    });
  }
}
